import { useTranslation } from "react-i18next";
import { useDietContext } from "@/contexts/DietContext";
import { useQuery } from "@tanstack/react-query";
import { diets } from "@/i18n/locales/diets";
import { useAuth } from "@/contexts/AuthContext";
import { Marked } from "marked";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Markdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import Prisma from "@prisma/client";

export const DietViewStep = () => {
    const { t, i18n } = useTranslation();
    const { user, currentDietId, authFetch } = useAuth();
    const [dietPlan, setDietPlan] = useState<string>(null);

    const navigate = useNavigate();

    const { data: currentDiet, isLoading: dietIsLoading } = useQuery({
        queryKey: ["diet", currentDietId],
        queryFn: async () => {
            const response = await authFetch(`/api/diet/${currentDietId}`);
            if (!response.ok) {
                throw new Error("Failed to fetch active diet");
            }
            return (await response.json()).diet as Prisma.Diet;
        },
        enabled: !!user,
    });

    const { data: guidelines, isLoading } = useQuery({
        queryKey: ["guidelines", currentDiet.type, i18n.language],
        queryFn: async () => {
            const guidelinesPath = `/guidelines/${currentDiet.type}/${i18n.language}.md`;
            const response = await fetch(guidelinesPath);
            if (!response.ok) {
                throw new Error("Failed to fetch guidelines");
            }
            return response.text();
        },
        enabled: !!currentDiet && !!currentDiet.type,
        retry: 1,
    });

    useEffect(() => {
        if (currentDiet && currentDiet.doc) {
            (async () => {
                const marked = new Marked({ gfm: true, breaks: true });
                setDietPlan(await marked.parse(currentDiet.doc));
            })();
        }
    }, [currentDiet]);

    let handleBackStep = () => {},
        handleFinish = () => {};

    let asView = true;
    try {
        const context = useDietContext();
        asView = !context;

        if (!asView) {
            const { setStep } = context;

            handleBackStep = () => {
                setStep((prev) => prev - 1);
            };

            handleFinish = () => {
                // Logica aggiuntiva se necessaria
                navigate("/dashboard");
            };
        }
    } catch (e) {}

    const exportToPDF = async () => {
        const newWindow = window.open("", "_blank", "width=800,height=600");
        newWindow.document.open();

        const md = `
        # ${t("buildDiet.viewDiet.dietOf", { name: user.name })}

        ## ${currentDietType.name}
        ${currentDietType.description}

        ${currentDiet.doc}

        ${guidelines}
        `.replace(/\n\s+/g, "\n");
        const marked = new Marked({ gfm: true, breaks: true });
        const html = await marked.parse(md);

        newWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>${t("buildDiet.viewDiet.dietOf", { name: user.name })}</title>
                <style>
                    h1, h2, h3 {
                        page-break-after: avoid;
                    }
                    table { width: 100%; border-collapse: collapse; }

                    td, th { padding: 2px 0.5em 0 2px; }
                    table tr td:nth-child(1),
                    table tr td:nth-child(3) {
                        width: 40%;
                    }

                    table tr td:nth-child(2),
                    table tr td:nth-child(4) {
                        width: 10%;
                    }
                    .footer {
                        margin-top: 2em;
                        color: hsl(120, 47.4%, 11.2%);
                        display: flex;
                        justify-content: center;
                    }
                    .footer img { width: 1em; height: 1em; }
                </style>
            </head>
            <body>
                ${html}
                <p class="footer">
                    &copy; 2025 (<img src="/favicon-32x32.png" alt="Dietopia" />) Dietopia
                </p>
            </body>
            </html>
        `);

        newWindow.document.close();
        newWindow.onload = () => {
            newWindow.print();
            newWindow.close();
        };
    };

    if (dietIsLoading || isLoading || !currentDiet || !guidelines || !dietPlan) {
        return (
            <div className="container mx-auto p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-8 bg-gray-200 rounded w-1/4"></div>
                    <div className="h-32 bg-gray-200 rounded"></div>
                    <div className="h-64 bg-gray-200 rounded"></div>
                </div>
            </div>
        );

        /*
        return (
            
            <div className="text-center">
                <h1 className="text-2xl font-bold text-gray-900 mb-4">
                    {t("buildDiet.noDiet")}
                </h1>
                <p className="text-gray-500">
                    {t("buildDiet.noDietDescription")}
                </p>
            </div>
        );
        */
    }

    const currentDietType = diets[i18n.language].find((d) => d.id === currentDiet.type);

    return (
        <div className="space-y-6">
            {!asView ? (
                <div className="text-center mb-12">
                    <h1 className="text-3xl font-bold text-gray-900 mb-4">{t("buildDiet.viewDiet.title")}</h1>
                    <p className="text-gray-500">{t("buildDiet.viewDiet.description")}</p>
                </div>
            ) : null}
            <Card className="p-6 space-y-4">
                <div className="flex">
                    <div className="flex-1">
                        <h3 className="text-xl font-semibold mb-2">{currentDietType.name}</h3>
                        <p className="text-gray-600">{currentDietType.description}</p>
                        <p className="text-sm text-gray-500">{currentDietType.objective}</p>
                    </div>
                    <Button onClick={exportToPDF}>{t("buildDiet.viewDiet.exportPDF")}</Button>
                </div>
            </Card>
            <Card className="p-6 space-y-4">
                <div className="prose max-w-none diet" dangerouslySetInnerHTML={{ __html: dietPlan }}></div>
                {/* <Markdown className="prose max-w-none diet">{currentDiet.doc}</Markdown> */}
            </Card>
            <Card className="p-6 space-y-4">
                <Markdown className="prose max-w-none">{guidelines}</Markdown>
            </Card>
            {/* Pulsanti Back e Finish */}
            {!asView ? (
                <div className="flex justify-between mt-6">
                    <Button variant="outline" onClick={handleBackStep}>
                        {t("buildDiet.back")}
                    </Button>
                    <Button onClick={handleFinish}>{t("buildDiet.finish")}</Button>
                </div>
            ) : null}
        </div>
    );
};
